import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Search, Edit, Trash2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = "/api";

const Leads = () => {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [budgetRanges, setBudgetRanges] = useState([]);
  const [timelines, setTimelines] = useState([]);

  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    service_type_id: '',
    budget_range: '',
    timeline_id: '',
    project_description: '',
    source: 'Website',
    status: 'New',
    internal_notes: '',
  });

  // ===============================
  // INITIAL LOAD
  // ===============================
  useEffect(() => {
    fetchLeads();
    fetchMasterData();
  }, []);

  // ===============================
  // FILTER WHEN DATA CHANGES
  // ===============================
  useEffect(() => {
    filterLeads();
  }, [leads, searchQuery, statusFilter]);

  // ===============================
  // FETCH MASTER DATA
  // ===============================
  const fetchMasterData = async () => {
    try {
      const [servicesRes, budgetsRes, timelinesRes] = await Promise.all([
        axios.get(`${API}/master/service-types`),
        axios.get(`${API}/master/budget-ranges`),
        axios.get(`${API}/master/timelines`)
      ]);

      setServiceTypes(servicesRes.data);
      setBudgetRanges(budgetsRes.data);
      setTimelines(timelinesRes.data);

    } catch (error) {
      toast.error("Failed to load master data");
    }
  };

  // ===============================
  // FETCH LEADS
  // ===============================
  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // FILTER LOGIC
  // ===============================
  const filterLeads = () => {
    let filtered = [...leads];

    if (searchQuery) {
      filtered = filtered.filter(
        (lead) =>
          lead.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((lead) => lead.status === statusFilter);
    }

    setFilteredLeads(filtered);
  };

  // ===============================
  // SUBMIT
  // ===============================
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingLead) {
        await axios.put(`${API}/leads/${editingLead.id}`, formData);
        toast.success('Lead updated successfully');
      } else {
        await axios.post(`${API}/leads`, formData);
        toast.success('Lead created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchLeads();

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save lead');
    }
  };

  // ===============================
  // DELETE
  // ===============================
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      await axios.delete(`${API}/leads/${id}`);
      toast.success('Lead deleted successfully');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  // ===============================
  // EDIT
  // ===============================
  const handleEdit = (lead) => {
    setEditingLead(lead);

    setFormData({
      ...lead,
      service_type_id: lead.service_type_id || '',
      timeline_id: lead.timeline_id || ''
    });

    setIsDialogOpen(true);
  };

  // ===============================
  // RESET FORM
  // ===============================
  const resetForm = () => {
    setEditingLead(null);

    setFormData({
      full_name: '',
      email: '',
      phone: '',
      service_type_id: '',
      budget_range: '',
      timeline_id: '',
      project_description: '',
      source: 'Website',
      status: 'New',
      internal_notes: '',
    });
  };
  
  const getStatusColor = (status) => {
    const colors = {
      New: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      Contacted: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      Qualified: 'bg-green-500/10 text-green-500 border-green-500/20',
      Lost: 'bg-red-500/10 text-red-500 border-red-500/20',
      Converted: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    };
    return colors[status] || 'bg-zinc-800 text-zinc-400 border-zinc-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:flex-1">
          <div className="relative w-full md:flex-1 md:max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-app-subtext w-4 h-4" />
            <Input
              placeholder="Search leads..."
              data-testid="search-leads"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900/50 border-zinc-800 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="leads-status-filter" className="w-full md:w-48 bg-zinc-900/50 border-zinc-800">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="New">New</SelectItem>
              <SelectItem value="Contacted">Contacted</SelectItem>
              <SelectItem value="Qualified">Qualified</SelectItem>
              <SelectItem value="Lost">Lost</SelectItem>
              <SelectItem value="Converted">Converted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-lead-button" className="w-full md:w-auto bg-blue-500 hover:bg-blue-600 text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]">
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingLead ? 'Edit Lead' : 'New Lead'}</DialogTitle>
              <DialogDescription className="sr-only">Add or edit lead details.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    data-testid="lead-fullname"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="bg-zinc-900/50 border-zinc-800 mt-1"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    data-testid="lead-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-zinc-900/50 border-zinc-800 mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    data-testid="lead-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-zinc-900/50 border-zinc-800 mt-1"
                  />
                </div>
                <div>
                  <Label>Service Type *</Label>
                  <Select value={formData.service_type_id} onValueChange={(val) => setFormData({ ...formData, service_type_id: val })}>
                    <SelectTrigger data-testid="lead-service-type" className="bg-zinc-900/50 border-zinc-800 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800">
                    {serviceTypes.map((service) => (
                    <SelectItem key={service.code} value={service.code}>
                   {service.name}
                  </SelectItem>
                         ))}
                   </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Budget Range</Label>
                  <Select value={formData.budget_range} onValueChange={(val) => setFormData({ ...formData, budget_range: val })}>
                    <SelectTrigger data-testid="lead-budget" className="bg-zinc-900/50 border-zinc-800 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                   <SelectContent className="bg-[#121212] border-zinc-800">
                  {budgetRanges.map((budget) => (
                  <SelectItem key={budget.id} value={budget.label}>
                  {budget.label}
                  </SelectItem>
                  ))}
                  </SelectContent>
                  </Select>
                </div>
                <div>
  <Label>Timeline</Label>

  <Select
    value={formData.timeline_id}
    onValueChange={(val) =>
      setFormData({ ...formData, timeline_id: val })
    }
  >
    <SelectTrigger
      data-testid="lead-timeline"
      className="bg-zinc-900/50 border-zinc-800 mt-1"
    >
      <SelectValue placeholder="Select timeline" />
    </SelectTrigger>

    <SelectContent className="bg-[#121212] border-zinc-800">
      {timelines.map((timeline) => (
        <SelectItem key={timeline.code} value={timeline.code}>
          {timeline.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
</div>
                <div>
                  <Label>Source</Label>
                  <Select value={formData.source} onValueChange={(val) => setFormData({ ...formData, source: val })}>
                    <SelectTrigger data-testid="lead-source" className="bg-zinc-900/50 border-zinc-800 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800">
                      <SelectItem value="Website">Website</SelectItem>
                      <SelectItem value="Instagram">Instagram</SelectItem>
                      <SelectItem value="Referral">Referral</SelectItem>
                      <SelectItem value="Direct">Direct</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                    <SelectTrigger data-testid="lead-status" className="bg-zinc-900/50 border-zinc-800 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800">
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Contacted">Contacted</SelectItem>
                      <SelectItem value="Qualified">Qualified</SelectItem>
                      <SelectItem value="Lost">Lost</SelectItem>
                      <SelectItem value="Converted">Converted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Project Description *</Label>
                <Textarea
                  data-testid="lead-description"
                  required
                  value={formData.project_description}
                  onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800 mt-1 min-h-[100px]"
                />
              </div>
              <div>
                <Label>Internal Notes</Label>
                <Textarea
                  data-testid="lead-notes"
                  value={formData.internal_notes}
                  onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800 mt-1"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" data-testid="cancel-lead-button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="border-zinc-700">
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-lead-button" className="bg-blue-500 hover:bg-blue-600">
                  {editingLead ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-400">Loading leads...</div>
      ) : filteredLeads.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">No leads found</div>
      ) : (
        <div className="bg-app-card border border-app-border rounded-lg overflow-hidden">
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-zinc-900/50 border-b border-zinc-800">
        <tr className="text-left text-xs uppercase tracking-wider text-app-subtext">
          <th className="px-6 py-4 w-[20%]">Name</th>
          <th className="px-6 py-4 w-[25%]">Contact</th>
          <th className="px-6 py-4 w-[15%]">Service</th>
          <th className="px-6 py-4 w-[12%]">Budget</th>
          <th className="px-6 py-4 w-[10%]">Source</th>
          <th className="px-6 py-4 w-[10%]">Status</th>
          <th className="px-6 py-4 w-[8%] text-right">Actions</th>
        </tr>
      </thead>

      <tbody className="divide-y divide-zinc-800/50">
        {filteredLeads.map((lead) => (
          <tr key={lead.id} className="hover:bg-app-muted transition-colors">
            <td className="px-6 py-4">
              <div className="font-medium text-zinc-100">
                {lead.full_name}
              </div>
              <div className="text-xs text-app-subtext">
                {new Date(lead.created_at).toLocaleDateString()}
              </div>
            </td>

            <td className="px-6 py-4">
              <div className="text-zinc-300 truncate">
                {lead.email}
              </div>
              {lead.phone && (
                <div className="text-xs text-app-subtext">
                  {lead.phone}
                </div>
              )}
            </td>

            <td className="px-6 py-4 text-zinc-300 whitespace-nowrap">
              {serviceTypes.find(s => s.code === lead.service_type_id)?.name || "—"}
            </td>

            <td className="px-6 py-4 text-zinc-300 whitespace-nowrap">
              {lead.budget_range || "N/A"}
            </td>

            <td className="px-6 py-4 text-zinc-300 whitespace-nowrap">
              {lead.source}
            </td>

            <td className="px-6 py-4">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(lead.status)}`}>
                {lead.status}
              </span>
            </td>

            <td className="px-6 py-4 text-right">
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => handleEdit(lead)}
                  className="text-blue-500 hover:text-blue-400 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(lead.id)}
                  className="text-red-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>
      )}
    </div>
  );
};

export default Leads;
